import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../store/realm-app";
import { LEGAL_PAGES } from "../legal/LegalPage";
import { PLACES, currentTab, placeNamed, placeOf, visibleTabs } from "./places";
import { Part, useFilter, usePlatform, usePlatformVersion } from "../platform/react";
import { useModerationCount } from "./moderation-count";
import { useShelf } from "../tools/pins";
import { useBandMenu } from "../marching/menu";
import Shelf from "../tools/Shelf";
import styles from "./places.module.css";

// Whether this browser has opened a book's practice page; the Books tab shows from then on.
const BOOKS = "truechops.books";
function useBooks(pathname, token) {
  const [books, setBooks] = useState(false);
  useEffect(() => {
    try {
      if (pathname === "/book" && token) localStorage.setItem(BOOKS, "1");
      setBooks(localStorage.getItem(BOOKS) === "1");
    } catch { /* Without storage, no Books tab. */ }
  }, [pathname, token]);
  return books;
}

// The tabs of the place the page is in, across the top of the page (docs/refactor-plan.md, Phase 1).
// You's also has logging out and the policies; in Make, the Score and the Song have the shelf of
// tools (Phase 2), out or away with Tools.
export default function PlaceTabs() {
  const router = useRouter(), dispatch = useDispatch();
  const user = useSelector((state) => state.realm.currentUser);
  const books = useBooks(router.pathname, router.query.token);
  const pending = useModerationCount(user?.isAdmin, router.asPath);
  const site = usePlatform(), places = useFilter("nav.places", PLACES), band = useBandMenu();
  usePlatformVersion();
  const place = placeNamed(placeOf(router.pathname, router.query, places), places), [shelf, changeShelf] = useShelf();
  const shelved = router.pathname === "/" || router.pathname === "/daw";
  // Pages that fill the window leave room for the tabs (see styles/App.css).
  useEffect(() => {
    if (place) document.documentElement.style.removeProperty("--site-places");
    else document.documentElement.style.setProperty("--site-places", "0px");
  }, [place]);
  if (!place) return null;
  const current = currentTab(router.pathname, router.query, places);
  return <div className={styles.placeBar}>
    <nav className={styles.placeTabs} aria-label={`${place.label} tabs`}>
      <span className={styles.placeName}>{place.label}</span>
      {visibleTabs(place.id, { user, books, appEnabled: site.appEnabled, places, ...band }).map((tab) => {
        const waiting = tab.id === "moderation" ? pending : 0;
        return <Link key={tab.id} href={tab.href} className={styles.tab} aria-current={tab.id === current ? "page" : undefined} aria-label={waiting ? `${tab.label}, ${waiting} waiting` : undefined}>
          {tab.label}{waiting > 0 && <span className={styles.count} aria-hidden="true">{waiting > 99 ? "99+" : waiting}</span>}</Link>;
      })}
      {place.id === "you" && user && <button type="button" className={styles.tab} onClick={() => { dispatch(logout()); router.push("/"); }}>Log Out</button>}
    </nav>
    {shelved && <Part name="site.rail.shelf" label="Tools"><button type="button" className={`${styles.tab} ${styles.toolsToggle}`} aria-pressed={shelf.open} onClick={() => changeShelf({ open: !shelf.open })}>Tools</button></Part>}
    {shelved && <Shelf />}
    {place.id === "you" && <nav className={styles.policies} aria-label="Site policies">
      {LEGAL_PAGES.map(([href, label]) => <Link key={href} href={href}>{label.replace("Copyright & DMCA", "Copyright").replace("Community ", "")}</Link>)}
    </nav>}
  </div>;
}
